// app/javascript/controllers/datepicker_controller.ts
import { Controller } from "@hotwired/stimulus"

interface AvailabilityDay {
  date: string;
  closed?: boolean;
  closed_to_arrival?: boolean;
  closed_to_departure?: boolean;
  min_los?: number;
  max_los?: number;
  available_count?: number;
}

/**
 * Datepicker Controller
 *
 * Key features:
 * - Loads availability data asynchronously via API (not embedded in page)
 * - Horizontal mode: 2 months side by side with pagination
 * - Vertical mode: Full-screen scrollable calendar for mobile (Airbnb-style)
 * - Auto-populates from hidden input values
 */
export default class extends Controller {
  static targets = ["calendar", "checkIn", "checkOut", "alert", "dateRangeDisplay", "clearButton", "modalContainer", "triggerButton"]
  static values = {
    propertyUuid: String,
    checkIn: String,
    checkOut: String,
    minStay: Number,
    loadAvailability: { type: Boolean, default: false },
    apiUrl: { type: String, default: '/api/v1/properties' },
    vertical: { type: Boolean, default: false },
    monthsToShow: { type: Number, default: 12 }
  }

  declare calendarTarget: HTMLElement;
  declare hasCalendarTarget: boolean;
  declare checkInTarget: HTMLInputElement;
  declare hasCheckInTarget: boolean;
  declare checkOutTarget: HTMLInputElement;
  declare hasCheckOutTarget: boolean;
  declare alertTarget: HTMLElement;
  declare hasAlertTarget: boolean;
  declare dateRangeDisplayTarget: HTMLElement;
  declare hasDateRangeDisplayTarget: boolean;
  declare clearButtonTarget: HTMLElement;
  declare hasClearButtonTarget: boolean;
  declare modalContainerTarget: HTMLElement;
  declare hasModalContainerTarget: boolean;
  declare triggerButtonTarget: HTMLElement;
  declare hasTriggerButtonTarget: boolean;

  declare propertyUuidValue: string;
  declare checkInValue: string;
  declare checkOutValue: string;
  declare minStayValue: number;
  declare loadAvailabilityValue: boolean;
  declare apiUrlValue: string;
  declare verticalValue: boolean;
  declare monthsToShowValue: number;

  private currentMonth!: Date;
  private loadedMonths!: Set<string>;
  private availabilityMap!: Record<string, AvailabilityDay>;
  private isLoading: boolean = false;
  private selectionMode: 'checkin' | 'checkout' | null = null;
  private calendarVisible: boolean = false;
  private focusedDate: Date | null = null;
  private handleOutsideClick!: (event: MouseEvent) => void;
  private handleModalClick!: (event: Event) => void;
  private hiddenArrivalDateInputs!: NodeListOf<HTMLInputElement>;
  private hiddenDepartureDateInputs!: NodeListOf<HTMLInputElement>;
  private modalElement: HTMLElement | null = null;

  connect(): void {
    this.currentMonth = new Date()
    this.currentMonth.setDate(1)
    this.loadedMonths = new Set()
    this.availabilityMap = {}
    this.isLoading = false
    this.selectionMode = null
    this.calendarVisible = false

    // Auto-populate from hidden input values
    this.initializeFromInputs()

    // Create modal element for vertical mode
    if (this.verticalValue) {
      this.createModalElement()
    }

    this.render()
    this.updateDateDisplay()

    this.handleOutsideClick = this.onOutsideClick.bind(this)
    document.addEventListener('click', this.handleOutsideClick)

    this.hiddenArrivalDateInputs = document.querySelectorAll("input[name='arrival_date']")
    this.hiddenDepartureDateInputs = document.querySelectorAll("input[name='departure_date']")
  }

  private initializeFromInputs(): void {
    // Read initial values from checkIn/checkOut target inputs
    if (this.hasCheckInTarget && this.checkInTarget.value) {
      this.checkInValue = this.checkInTarget.value
    }
    if (this.hasCheckOutTarget && this.checkOutTarget.value) {
      this.checkOutValue = this.checkOutTarget.value
    }

    // If we have a check-in date, navigate the calendar to that month
    if (this.checkInValue) {
      const checkInDate = new Date(this.checkInValue)
      this.currentMonth = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), 1)
    }
  }

  private createModalElement(): void {
    // Check if modal container target exists, otherwise create and append to body
    if (this.hasModalContainerTarget) {
      this.modalElement = this.modalContainerTarget
    } else {
      this.modalElement = document.createElement('div')
      this.modalElement.className = 'datepicker-modal-overlay hidden'
      document.body.appendChild(this.modalElement)
    }

    // Attach click handler for modal interactions (since it's outside controller scope)
    this.handleModalClick = this.onModalClick.bind(this)
    this.modalElement.addEventListener('click', this.handleModalClick)
  }

  private onModalClick(event: Event): void {
    const target = event.target as HTMLElement

    // Handle close button
    if (target.closest('.datepicker-close-btn')) {
      event.preventDefault()
      event.stopPropagation()
      this.hideCalendar()
      return
    }

    // Handle clear dates button
    if (target.closest('.datepicker-clear-link')) {
      event.preventDefault()
      event.stopPropagation()
      this.checkInValue = ''
      this.checkOutValue = ''
      this.selectionMode = null
      this.updateDateDisplay()
      this.render()
      this.dispatchChangeEvent()
      this.updateHiddenInputs(this.checkInValue, this.checkOutValue)
      return
    }

    // Handle save button
    if (target.closest('.datepicker-save-btn')) {
      event.preventDefault()
      event.stopPropagation()
      this.hideCalendar()
      return
    }

    // Handle date selection
    const dayCell = target.closest('.day-cell') as HTMLElement
    if (dayCell && !dayCell.dataset.disabled) {
      event.preventDefault()
      event.stopPropagation()
      const dateStr = dayCell.dataset.date
      if (dateStr) {
        this.handleDateSelection(dateStr)
      }
      return
    }
  }

  private handleDateSelection(dateStr: string): void {
    if (!this.checkInValue) {
      this.checkInValue = dateStr
      this.selectionMode = 'checkout'
    } else if (!this.checkOutValue) {
      const checkIn = new Date(this.checkInValue)
      const checkOut = new Date(dateStr)

      if (checkOut <= checkIn) {
        this.checkInValue = dateStr
        this.checkOutValue = ''
        this.selectionMode = 'checkout'
      } else {
        this.checkOutValue = dateStr
        this.selectionMode = null

        if (!this.verticalValue) {
          this.hideCalendar()
        }

        this.dispatchChangeEvent()
      }
    } else {
      this.checkInValue = dateStr
      this.checkOutValue = ''
      this.selectionMode = 'checkout'
    }

    this.updateDateDisplay()
    this.updateHiddenInputs(this.checkInValue, this.checkOutValue)
    this.render()
  }

  disconnect(): void {
    document.removeEventListener('click', this.handleOutsideClick)
    // Clean up modal event listener and element
    if (this.modalElement) {
      this.modalElement.removeEventListener('click', this.handleModalClick)
      if (!this.hasModalContainerTarget) {
        this.modalElement.remove()
      }
    }
  }

  private onOutsideClick(event: MouseEvent): void {
    const target = event.target as Node
    // Don't close if clicking inside the element or the modal
    if (this.element.contains(target)) return
    if (this.modalElement && this.modalElement.contains(target)) return

    if (this.calendarVisible) {
      this.hideCalendar()
    }
  }

  toggleCalendar(event: Event): void {
    event.stopPropagation()
    if (this.calendarVisible) {
      this.hideCalendar()
    } else {
      this.showCalendar()
    }
  }

  private setExpanded(expanded: boolean): void {
    if (this.hasTriggerButtonTarget) {
      this.triggerButtonTarget.setAttribute('aria-expanded', expanded ? 'true' : 'false')
    }
  }

  async showCalendar(): Promise<void> {
    this.calendarVisible = true
    this.setExpanded(true)

    // The roving-tabindex day: the current check-in if set, otherwise today. This
    // is the day the grid lands on when the user tabs into it (WCAG 2.1.1).
    this.focusedDate = this.checkInValue ? new Date(`${this.checkInValue}T00:00:00`) : new Date()

    if (this.verticalValue && this.modalElement) {
      // Render into full-screen modal
      this.modalElement.classList.remove('hidden')
      this.modalElement.innerHTML = this.buildVerticalCalendarHTML()
      document.body.style.overflow = 'hidden'

      if (this.loadAvailabilityValue && this.propertyUuidValue) {
        await this.ensureMonthsLoaded(this.currentMonth, this.monthsToShowValue)
      }
    } else {
      // Render into inline calendar target
      this.calendarTarget.classList.remove('hidden')
      this.render()
      this.positionCalendar()

      if (this.loadAvailabilityValue && this.propertyUuidValue) {
        await this.ensureMonthsLoaded(this.currentMonth, 2)
      }
    }
  }

  private positionCalendar(): void {
    requestAnimationFrame(() => {
      const calendarRect = this.calendarTarget.getBoundingClientRect()
      const triggerRect = this.element.getBoundingClientRect()
      const viewportWidth = window.innerWidth

      const wouldOverflowRight = triggerRect.left + calendarRect.width > viewportWidth - 20

      if (wouldOverflowRight) {
        this.calendarTarget.style.left = 'auto'
        this.calendarTarget.style.right = '0'
      } else {
        this.calendarTarget.style.left = '0'
        this.calendarTarget.style.right = 'auto'
      }
    })
  }

  hideCalendar(): void {
    this.calendarVisible = false
    this.setExpanded(false)

    if (this.verticalValue && this.modalElement) {
      this.modalElement.classList.add('hidden')
      this.modalElement.innerHTML = ''
      document.body.style.overflow = ''
    } else {
      this.calendarTarget.classList.add('hidden')
    }
  }

  clearDates(event: Event): void {
    event.stopPropagation()
    this.checkInValue = ''
    this.checkOutValue = ''
    this.selectionMode = null
    this.updateDateDisplay()
    this.render()
    this.dispatchChangeEvent()
    this.updateHiddenInputs(this.checkInValue, this.checkOutValue)
  }

  save(event: Event): void {
    event.stopPropagation()
    this.hideCalendar()
  }

  updateDateDisplay(): void {
    if (!this.hasDateRangeDisplayTarget) return

    if (this.checkInValue && this.checkOutValue) {
      const checkIn = new Date(this.checkInValue)
      const checkOut = new Date(this.checkOutValue)
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
      this.dateRangeDisplayTarget.textContent = `${formatter.format(checkIn)} — ${formatter.format(checkOut)}`
      if (this.hasClearButtonTarget) {
        this.clearButtonTarget.classList.remove('hidden')
      }
    } else if (this.checkInValue) {
      const checkIn = new Date(this.checkInValue)
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
      this.dateRangeDisplayTarget.textContent = `${formatter.format(checkIn)} — Add checkout`
      if (this.hasClearButtonTarget) {
        this.clearButtonTarget.classList.remove('hidden')
      }
    } else {
      this.dateRangeDisplayTarget.textContent = 'Check In — Out'
      if (this.hasClearButtonTarget) {
        this.clearButtonTarget.classList.add('hidden')
      }
    }
  }

  private getNightsCount(): number {
    if (!this.checkInValue || !this.checkOutValue) return 0
    const checkIn = new Date(this.checkInValue)
    const checkOut = new Date(this.checkOutValue)
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))
  }

  private getHeaderContent(): string {
    const nightsCount = this.getNightsCount()

    if (this.checkInValue && this.checkOutValue) {
      const checkIn = new Date(this.checkInValue)
      const checkOut = new Date(this.checkOutValue)
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      return `
        <div class="text-2xl font-semibold">${nightsCount} night${nightsCount !== 1 ? 's' : ''}</div>
        <div class="text-sm text-gray-500">${formatter.format(checkIn)} - ${formatter.format(checkOut)}</div>
      `
    } else if (this.checkInValue) {
      const checkIn = new Date(this.checkInValue)
      const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      return `
        <div class="text-2xl font-semibold">Select checkout</div>
        <div class="text-sm text-gray-500">Check-in: ${formatter.format(checkIn)}</div>
      `
    } else {
      return `
        <div class="text-2xl font-semibold">Select dates</div>
        <div class="text-sm text-gray-500">Add your travel dates for exact pricing</div>
      `
    }
  }

  async ensureMonthsLoaded(startMonth: Date, monthCount: number = 2): Promise<void> {
    const monthsToLoad: Date[] = []

    for (let i = 0; i < monthCount; i++) {
      const month = new Date(startMonth)
      month.setMonth(month.getMonth() + i)
      const monthKey = this.getMonthKey(month)

      if (!this.loadedMonths.has(monthKey)) {
        monthsToLoad.push(month)
      }
    }

    if (monthsToLoad.length === 0) {
      return
    }

    const firstMonth = monthsToLoad[0]
    const lastMonth = monthsToLoad[monthsToLoad.length - 1]

    const fromDate = this.getFirstDayOfMonth(firstMonth)
    const toDate = this.getLastDayOfMonth(lastMonth)

    await this.loadAvailabilityRange(fromDate, toDate, monthsToLoad)
  }

  async loadAvailabilityRange(fromDate: Date, toDate: Date, monthsToLoad: Date[]): Promise<void> {
    if (this.isLoading) {
      return
    }

    if (!this.propertyUuidValue) {
      console.warn('No property UUID provided for availability loading')
      return
    }

    this.isLoading = true
    this.showLoadingState()

    try {
      const offset = this.daysBetween(new Date(), fromDate)
      const limit = this.daysBetween(fromDate, toDate) + 1

      const url = `${this.apiUrlValue}/${this.propertyUuidValue}/availability/?offset=${offset}&limit=${limit}`

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      if (data.results && Array.isArray(data.results)) {
        data.results.forEach((day: AvailabilityDay) => {
          this.availabilityMap[day.date] = day
        })
      }

      monthsToLoad.forEach(month => {
        this.loadedMonths.add(this.getMonthKey(month))
      })

      this.isLoading = false
      this.hideLoadingState()
      this.render()
    } catch (error) {
      console.error('Failed to load availability:', error)
      this.isLoading = false
      this.hideLoadingState()
    }
  }

  async previousMonth(event: Event): Promise<void> {
    event.stopPropagation()
    if (this.isPreviousMonthDisabled()) {
      return
    }

    this.currentMonth.setMonth(this.currentMonth.getMonth() - 1)
    this.render()

    if (this.loadAvailabilityValue && this.propertyUuidValue) {
      await this.ensureMonthsLoaded(this.currentMonth, 2)
    }
  }

  async nextMonth(event: Event): Promise<void> {
    event.stopPropagation()
    this.currentMonth.setMonth(this.currentMonth.getMonth() + 1)
    this.render()

    if (this.loadAvailabilityValue && this.propertyUuidValue) {
      await this.ensureMonthsLoaded(this.currentMonth, 2)
    }
  }

  private getMonthKey(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${year}-${month}`
  }

  private getFirstDayOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  private getLastDayOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)
  }

  private daysBetween(date1: Date, date2: Date): number {
    const oneDay = 24 * 60 * 60 * 1000
    return Math.round((date2.getTime() - date1.getTime()) / oneDay)
  }

  private showLoadingState(): void {
    if (this.hasCalendarTarget) {
      this.calendarTarget.classList.add('loading')
    }
  }

  private hideLoadingState(): void {
    if (this.hasCalendarTarget) {
      this.calendarTarget.classList.remove('loading')
    }
  }

  private render(): void {
    if (this.verticalValue && this.modalElement && this.calendarVisible) {
      this.modalElement.innerHTML = this.buildVerticalCalendarHTML()
    } else if (this.hasCalendarTarget) {
      this.calendarTarget.innerHTML = this.buildCalendarHTML()
    }
    this.updateAlert()
  }

  private buildCalendarHTML(): string {
    if (this.verticalValue) {
      return this.buildVerticalCalendarHTML()
    }
    return this.buildHorizontalCalendarHTML()
  }

  private buildHorizontalCalendarHTML(): string {
    return `
      <div class="datepicker-container ${this.isLoading ? 'loading' : ''}">
        ${this.buildMonthNavigation()}
        <div class="calendar-grid">
          ${this.buildMonthView(this.currentMonth)}
          ${this.buildMonthView(this.getNextMonth())}
        </div>
        ${this.isLoading ? '<div class="loading-indicator">Loading...</div>' : ''}
      </div>
    `
  }

  private buildVerticalCalendarHTML(): string {
    const months: string[] = []
    const startMonth = new Date()
    startMonth.setDate(1)

    for (let i = 0; i < this.monthsToShowValue; i++) {
      const monthDate = new Date(startMonth)
      monthDate.setMonth(monthDate.getMonth() + i)
      months.push(this.buildVerticalMonthView(monthDate))
    }

    return `
      <div class="datepicker-container datepicker-vertical">
        <div class="datepicker-vertical-header">
          <button type="button" class="datepicker-close-btn" data-action="click->datepicker#hideCalendar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <button type="button" class="datepicker-clear-link" data-action="click->datepicker#clearDates">
            Clear dates
          </button>
          <div class="datepicker-header-content">
            ${this.getHeaderContent()}
          </div>
        </div>
        <div class="datepicker-sticky-weekdays">
          ${this.buildWeekdayHeaders()}
        </div>
        <div class="datepicker-vertical-scroll">
          ${months.join('')}
        </div>
        <div class="datepicker-vertical-footer">
          <button type="button" class="datepicker-save-btn" data-action="click->datepicker#save">
            Save
          </button>
        </div>
      </div>
    `
  }

  private buildWeekdayHeaders(): string {
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    return `
      <div class="weekday-headers" aria-hidden="true">
        ${weekDays.map(day => `<div class="weekday-header">${day}</div>`).join('')}
      </div>
    `
  }

  private buildVerticalMonthView(monthDate: Date): string {
    const year = monthDate.getFullYear()
    const month = monthDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })

    let html = `<div class="month-view-vertical">`
    html += `<div class="month-title">${formatter.format(monthDate)}</div>`
    html += `<div class="days-grid" role="group" aria-label="${formatter.format(monthDate)}">`

    for (let i = 0; i < startPadding; i++) {
      html += '<div class="day-cell empty" aria-hidden="true"></div>'
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day)
      html += this.buildDayCell(date, this.availabilityMap[this.formatDate(date)])
    }

    html += '</div></div>'
    return html
  }

  private buildMonthNavigation(): string {
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
    const currentMonthName = formatter.format(this.currentMonth)
    const nextMonthName = formatter.format(this.getNextMonth())
    const showPreviousButton = !this.isPreviousMonthDisabled()

    return `
      <div class="month-navigation">
        ${showPreviousButton ? `
          <button type="button"
                  aria-label="Previous month"
                  data-action="click->datepicker#previousMonth"
                  class="nav-button">
            <svg width="16" height="16" fill="currentColor" aria-hidden="true">
              <path d="M10.5 3L5.5 8l5 5" stroke="currentColor" stroke-width="2" fill="none"/>
            </svg>
          </button>
        ` : '<div class="nav-button-spacer"></div>'}
        <div class="month-names">
          <span>${currentMonthName}</span>
          <span>${nextMonthName}</span>
        </div>
        <button type="button"
                aria-label="Next month"
                data-action="click->datepicker#nextMonth"
                class="nav-button">
          <svg width="16" height="16" fill="currentColor" aria-hidden="true">
            <path d="M5.5 3l5 5-5 5" stroke="currentColor" stroke-width="2" fill="none"/>
          </svg>
        </button>
      </div>
    `
  }

  private buildMonthView(monthDate: Date): string {
    const year = monthDate.getFullYear()
    const month = monthDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startPadding = firstDay.getDay()

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(monthDate)
    let html = '<div class="month-view">'

    // Weekday initials are decorative — each day's aria-label carries its weekday.
    html += '<div class="weekday-headers" aria-hidden="true">'
    weekDays.forEach(day => {
      html += `<div class="weekday-header">${day}</div>`
    })
    html += '</div>'

    html += `<div class="days-grid" role="group" aria-label="${monthLabel}">`

    for (let i = 0; i < startPadding; i++) {
      html += '<div class="day-cell empty" aria-hidden="true"></div>'
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day)
      html += this.buildDayCell(date, this.availabilityMap[this.formatDate(date)])
    }

    html += '</div></div>'
    return html
  }

  private getDayClasses(date: Date, availability?: AvailabilityDay): string {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dateStr = this.formatDate(date)

    const classes: string[] = []

    if (date < today) {
      classes.push('past')
    }

    if (this.loadAvailabilityValue) {
      if (availability) {
        if (availability.closed) {
          classes.push('closed')
        }
        if (availability.available_count === 0) {
          classes.push('unavailable')
        }
      }
    }

    if (dateStr === this.checkInValue) {
      classes.push('check-in')
    }

    if (dateStr === this.checkOutValue) {
      classes.push('check-out')
    }

    if (this.checkInValue && this.checkOutValue) {
      const checkIn = new Date(this.checkInValue)
      const checkOut = new Date(this.checkOutValue)
      if (date > checkIn && date < checkOut) {
        classes.push('in-range')
      }
    }

    return classes.join(' ')
  }

  private isDayDisabled(date: Date, availability?: AvailabilityDay): boolean {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (date < today) {
      return true
    }

    if (this.loadAvailabilityValue && availability) {
      if (availability.available_count === 0 || availability.closed) {
        return true
      }

      if (this.selectionMode === 'checkout' && availability.closed_to_arrival) {
        return true
      }

      if (this.selectionMode === 'checkin' && availability.closed_to_departure) {
        return true
      }
    }

    return false
  }

  selectDate(event: Event): void {
    event.stopPropagation()
    const target = event.currentTarget as HTMLElement
    if (target.getAttribute('aria-disabled') === 'true') return
    const dateStr = target.dataset.date
    if (!dateStr) return
    this.focusedDate = new Date(`${dateStr}T00:00:00`)
    this.handleDateSelection(dateStr)
    // handleDateSelection re-renders; keep focus on the day if the calendar is
    // still open (it closes once a full range is chosen in horizontal mode).
    if (this.calendarVisible) this.focusDayButton(this.focusedDate)
  }

  // Accessible day cell. Kept as a <div> (so the existing .day-cell CSS is
  // untouched) but exposed as role="button" with a full-date aria-label and a
  // roving tabindex; keydown handles activation + arrow navigation. data-disabled
  // stays for the CSS; aria-disabled mirrors it for AT (WCAG 2.1.1 / 4.1.2).
  private buildDayCell(date: Date, availability?: AvailabilityDay): string {
    const dateStr = this.formatDate(date)
    const classes = this.getDayClasses(date, availability)
    const isDisabled = this.isDayDisabled(date, availability)
    const isFocusTarget = this.focusedDate !== null && this.isSameDay(date, this.focusedDate)
    const action = isDisabled
      ? 'keydown->datepicker#handleDayKeydown'
      : 'click->datepicker#selectDate keydown->datepicker#handleDayKeydown'
    return `
      <div class="day-cell ${classes}"
           role="button"
           tabindex="${isFocusTarget ? '0' : '-1'}"
           aria-label="${this.getDayAriaLabel(date, isDisabled)}"
           ${isDisabled ? 'aria-disabled="true" data-disabled="true"' : ''}
           data-date="${dateStr}"
           data-action="${action}">
        <span class="day-number" aria-hidden="true">${date.getDate()}</span>
      </div>
    `
  }

  private getDayAriaLabel(date: Date, isDisabled: boolean): string {
    const fmt = new Intl.DateTimeFormat('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    })
    let label = fmt.format(date)
    const dateStr = this.formatDate(date)
    if (dateStr === this.checkInValue) {
      label += ', check-in date'
    } else if (dateStr === this.checkOutValue) {
      label += ', check-out date'
    } else if (this.checkInValue && this.checkOutValue) {
      const ci = new Date(`${this.checkInValue}T00:00:00`)
      const co = new Date(`${this.checkOutValue}T00:00:00`)
      if (date > ci && date < co) label += ', in selected range'
    }
    if (isDisabled) label += ', unavailable'
    return label
  }

  handleDayKeydown(event: KeyboardEvent): void {
    const target = event.currentTarget as HTMLElement
    const dateStr = target.dataset.date
    if (!dateStr) return
    const current = new Date(`${dateStr}T00:00:00`)

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      if (target.getAttribute('aria-disabled') !== 'true') {
        this.focusedDate = current
        this.handleDateSelection(dateStr)
        if (this.calendarVisible) this.focusDayButton(current)
      }
      return
    }

    let next: Date | null = null
    switch (event.key) {
      case 'ArrowRight': next = this.addDays(current, 1); break
      case 'ArrowLeft': next = this.addDays(current, -1); break
      case 'ArrowDown': next = this.addDays(current, 7); break
      case 'ArrowUp': next = this.addDays(current, -7); break
      case 'Home': next = this.addDays(current, -current.getDay()); break
      case 'End': next = this.addDays(current, 6 - current.getDay()); break
      case 'PageUp': next = new Date(current.getFullYear(), current.getMonth() - 1, current.getDate()); break
      case 'PageDown': next = new Date(current.getFullYear(), current.getMonth() + 1, current.getDate()); break
      default: return
    }
    event.preventDefault()
    if (next) this.focusDate(next)
  }

  private focusDate(newDate: Date): void {
    // Don't navigate into fully-past months.
    const firstAllowed = new Date()
    firstAllowed.setDate(1)
    firstAllowed.setHours(0, 0, 0, 0)
    if (newDate < firstAllowed) return

    this.focusedDate = newDate

    if (!this.verticalValue) {
      // Shift the two-month view if the focus moved past either visible edge.
      const secondMonthEnd = this.getLastDayOfMonth(this.getNextMonth())
      if (newDate < this.currentMonth || newDate > secondMonthEnd) {
        this.currentMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1)
        if (this.loadAvailabilityValue && this.propertyUuidValue) {
          this.ensureMonthsLoaded(this.currentMonth, 2)
        }
      }
    }
    this.render()
    this.focusDayButton(newDate)
  }

  private focusDayButton(date: Date): void {
    const container = this.verticalValue && this.modalElement
      ? this.modalElement
      : (this.hasCalendarTarget ? this.calendarTarget : null)
    if (!container) return
    const btn = container.querySelector<HTMLElement>(`[data-date="${this.formatDate(date)}"]`)
    if (btn) btn.focus()
  }

  private addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate()
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  private getNextMonth(): Date {
    const next = new Date(this.currentMonth)
    next.setMonth(next.getMonth() + 1)
    return next
  }

  private isPreviousMonthDisabled(): boolean {
    const today = new Date()
    today.setDate(1)
    today.setHours(0, 0, 0, 0)
    return this.currentMonth <= today
  }

  private updateAlert(): void {
    // Implement alert logic if needed
  }

  private dispatchChangeEvent(): void {
    this.element.dispatchEvent(new CustomEvent('datechange', {
      detail: {
        checkIn: this.checkInValue,
        checkOut: this.checkOutValue
      },
      bubbles: true
    }))
  }

  private updateHiddenInputs(arrivalDate: string, departureDate: string): void {
    this.hiddenArrivalDateInputs.forEach(input => {
      input.value = arrivalDate || ''
    })
    this.hiddenDepartureDateInputs.forEach(input => {
      input.value = departureDate || ''
    })
  }
}
